import { z } from "zod";

import { isValidCnpj, isValidCpf } from "@/shared/utils/brazilDocument";
import { isValidClabe } from "@/shared/utils/clabe";
import { pixKeyValid, type PixKeyTypeEnum } from "@/shared/utils/pixKey";

import { CbuCvuAccountIdType } from "../../services/paymentsPayout.service";

/**
 * The destinations the runtime accepts for PIX, SPEI and CBU/CVU (`CreatePayoutDto`), stated as Zod contracts
 * so the forms and their tests share one rule. Messages are injected, as in `transfiyaRecipientSchema`, to
 * keep these testable without an i18n runtime.
 */

export const PIX_KEY_TYPES = [
  "CPF",
  "CNPJ",
  "PHONE",
  "EMAIL",
  "EVP",
] as const satisfies readonly PixKeyTypeEnum[];

/** Shape and, for a document, its check digits: a CPF with the right length but a typo is still refused. */
export function isPixKeyComplete(type: PixKeyTypeEnum, value: string): boolean {
  if (!pixKeyValid(type, value)) return false;
  if (type === "CPF") return isValidCpf(value);
  if (type === "CNPJ") return isValidCnpj(value);
  return true;
}

export interface PixFormMessages {
  /** Per type: what is wrong with the key. */
  key: Record<PixKeyTypeEnum, string>;
  /** A document key with the right length whose check digits do not add up. */
  keyCheck: string;
  name: string;
}

export function pixRecipientSchema(messages: PixFormMessages) {
  return z
    .object({
      keyType: z.enum(PIX_KEY_TYPES),
      keyValue: z.string(),
      beneficiaryName: z.string().trim().min(2, messages.name).max(120, messages.name),
    })
    .superRefine((values, ctx) => {
      if (isPixKeyComplete(values.keyType, values.keyValue)) return;
      const wellFormed = pixKeyValid(values.keyType, values.keyValue);
      ctx.addIssue({
        code: "custom",
        path: ["keyValue"],
        message: wellFormed ? messages.keyCheck : messages.key[values.keyType],
      });
    });
}

export type PixRecipientValues = z.input<ReturnType<typeof pixRecipientSchema>>;

export const EMPTY_PIX_VALUES: PixRecipientValues = { keyType: "CPF", keyValue: "", beneficiaryName: "" };

/** An RFC is 12 characters for a company and 13 for a person; the backend caps it at 13. */
const RFC_PATTERN = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;

export interface SpeiFormMessages {
  clabe: string;
  clabeCheck: string;
  name: string;
  rfc: string;
}

export function speiRecipientSchema(messages: SpeiFormMessages) {
  return z.object({
    clabe: z
      .string()
      .regex(/^\d{18}$/, messages.clabe)
      .refine(isValidClabe, messages.clabeCheck),
    beneficiaryName: z.string().trim().min(2, messages.name).max(120, messages.name),
    rfc: z
      .string()
      .trim()
      .refine((value) => value === "" || RFC_PATTERN.test(value), messages.rfc),
  });
}

export type SpeiRecipientValues = z.input<ReturnType<typeof speiRecipientSchema>>;

export const EMPTY_SPEI_VALUES: SpeiRecipientValues = { clabe: "", beneficiaryName: "", rfc: "" };

export const CBU_CVU_TYPES = [CbuCvuAccountIdType.CVU, CbuCvuAccountIdType.CBU] as const;

export interface CbuCvuFormMessages {
  accountNumber: string;
  cuit: string;
  name: string;
}

export function cbuCvuRecipientSchema(messages: CbuCvuFormMessages) {
  return z.object({
    accountType: z.enum(CBU_CVU_TYPES),
    accountNumber: z.string().regex(/^\d{22}$/, messages.accountNumber),
    cuitCuil: z.string().refine((value) => value === "" || /^\d{11}$/.test(value), messages.cuit),
    beneficiaryName: z.string().trim().min(2, messages.name).max(120, messages.name),
  });
}

export type CbuCvuRecipientValues = z.input<ReturnType<typeof cbuCvuRecipientSchema>>;

export const EMPTY_CBU_CVU_VALUES: CbuCvuRecipientValues = {
  accountType: CbuCvuAccountIdType.CVU,
  accountNumber: "",
  cuitCuil: "",
  beneficiaryName: "",
};
